import { differenceInDays } from 'date-fns';

import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import { registerInteraction } from 'services/interactions.service';
import { useModal } from 'context/ModalContext';
import { getKPIHistoryPag, getServerTime } from 'services/dashboard.service';
import { TbMessageCancel, TbSearch } from 'react-icons/tb';
import { convertEmojis, itemsPerPageExtrato } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { FiEdit3 } from 'react-icons/fi';
import { BsArrowReturnLeft } from 'react-icons/bs';
import ModalCancelRecognition from 'components/modal-cancel-recognition';
import ModalComment from 'components/modal-comment';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import * as ui from 'components/reconhecimento-ui';
import BuscaDePessoas from 'components/ui/busca-de-pessoas';
import { useRequisicaoMaisRecente } from 'hooks/useRequisicaoMaisRecente';
import { TITULO_DE_SECAO } from 'styles';

const CoinLabel: any = {
  uau: '#UAU',
  mandou: '#MANDOUBEM',
  valeu: '#VALEU',
};

const BehaviorLabel: any = {
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  foco: 'Foco no Cliente',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

interface Props {
  filter?: any;
}

export default function RecognitionReceived(data: Props) {
  const { filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const { showModal } = useModal();
  const [loadingList, setLoadingList] = useState(true);
  const [total, setTotal] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [time, setTime] = useState<number>();
  const [returnRecognitionId, setReturnRecognitionId] = useState<string | undefined>();

  const [modalCommentObj, setModalCommentObj] = useState<any>();
  const [loadingInteraction, setLoadingInteraction] = useState<boolean | undefined>(false);

  // Ref para controlar se o carregamento inicial já foi feito
  const initialLoadDone = useRef(false);
  // Trocar a chave remonta o campo de busca vazio, depois de um cancelamento.
  const [versaoDaBusca, setVersaoDaBusca] = useState(0);
  const requisicoes = useRequisicaoMaisRecente();

  // Carrega o horário do servidor
  useEffect(() => {
    async function loadTime() {
      const res = await getServerTime();
      setTime(res);
    }

    if (user && currentCycle) {
      loadTime();
    }
  }, [currentCycle, user]);

  const hasLike = useCallback(
    (item: any) => {
      return !!item?.interaction?.find((i: any) => i.sender === user?._id && !!i.like);
    },
    [user?._id],
  );

  function canEdit(item: any) {
    if (item && time) {
      const differenceTime = differenceInDays(new Date(time), new Date(item.created));
      return differenceTime <= 10;
    }
    return false;
  }

  const sendInteraction = useCallback(
    async (item: any, type: string, message?: string) => {
      setLoadingInteraction(true);
      const index = list?.findIndex((i: any) => i._id === item._id);

      let payload: any;
      const newList = [...list];
      const interactionItem = item.interaction[0];

      if (!item?.interaction?.length && item?.action_id) {
        payload = {
          item: item.action_id as string,
          player: item.sender as string,
          sender: user?._id as string,
          senderName: user?.name as string,
          like: type === 'like' && hasLike(item) ? false : type === 'like' && !hasLike(item) ? true : hasLike(item),
          message: type === 'message' && typeof message === 'string' && message.trim().length ? message.trim() : undefined,
          created: {
            $date: new Date().getTime(),
          },
        };
      } else if (item?.interaction?.length && item?.action_id && interactionItem?._id) {
        if (type === 'like') {
          payload = {
            ...interactionItem,
            like: !hasLike(item),
            updated: {
              $date: new Date().getTime(),
            },
          };
        } else if (type === 'message') {
          payload = {
            ...interactionItem,
            message,
            updated: {
              $date: new Date().getTime(),
            },
          };
        }

        if (typeof payload?.created === 'number') {
          payload.created = {
            $date: payload.created,
          };
        }
      }

      if (payload && payload.created) {
        const data = await registerInteraction(payload);

        if (newList[index]?.interaction?.length) {
          newList[index].interaction[0] = data;
        } else {
          newList[index].interaction = [data];
        }

        setList(newList);
      }

      setLoadingInteraction(false);
    },
    [hasLike, list, user?._id, user?.name],
  );

  function getComment(item: any) {
    const interaction = item.interaction?.find((e: any) => !!e.message?.trim().length);
    return interaction || null;
  }

  function handleDetail(item: any) {
    showModal('extrato-detalhe', item);
  }

  function getReturnComment(item: any) {
    if (item?.cancelled && item?.returnRecognition?.message) {
      return item?.returnRecognition?.message;
    }
    return null;
  }

  const isOldVersion = (item: any): boolean => {
    return !!item.proximidade && !!item.hierarquia && !!item.total;
  };

  // Função para carregar dados - recebe parâmetros explícitos para evitar problemas de timing
  const fetchData = useCallback(
    async (searchTerm: string, loadMore: boolean, currentList: any[]) => {
      const ehAUltima = requisicoes.iniciar();
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoadingList(true);
      }

      try {
        // Monta o filtro com base nos parâmetros
        const filterPayload: any = {
          ...filter,
          cycleId: filter?.cycleId || currentCycle?._id,
        };

        if (searchTerm.trim()) {
          filterPayload.search = searchTerm.trim();
        }

        // Se for "carregar mais", busca o restante (10000 para pegar tudo)
        // Se for primeira carga, busca apenas itemsPerPageExtrato
        const rangeStart = loadMore ? currentList.length : 0;
        const rangeTotal = loadMore ? 10000 : itemsPerPageExtrato;

        const response = await getKPIHistoryPag(
          user?._id,
          filterPayload,
          rangeStart,
          rangeTotal,
          currentCycle?.date_start,
          currentCycle?.date_finish,
        );
        if (!ehAUltima()) return;

        if (loadMore) {
          // Concatena com a lista existente
          setList([...currentList, ...response.result]);
          setAllLoaded(true); // Após carregar mais, já carregou tudo
        } else {
          // Primeira carga ou nova busca: substitui a lista
          setList(response.result);
          // Se o total for maior que o que carregamos, há mais itens
          setAllLoaded(response.range.total <= itemsPerPageExtrato);
        }

        setTotal(response.range.total);
      } catch (error) {
        console.error('Erro ao carregar reconhecimentos:', error);
      } finally {
        if (ehAUltima()) {
          setLoadingList(false);
          setLoadingMore(false);
        }
      }
    },
    [currentCycle?._id, currentCycle?.date_finish, currentCycle?.date_start, filter, user?._id, requisicoes],
  );

  // Carregamento inicial - só executa uma vez quando as dependências estão prontas
  useEffect(() => {
    if (user && currentCycle && filter?.cycleId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchData('', false, []);
    }
  }, [user, currentCycle, filter?.cycleId, fetchData]);

  // Busca pelo texto do campo, já aparado (AJ2-46); termo vazio volta à lista inteira (AJ2-47).
  const buscar = (termo: string) => {
    setSearchValue(termo);
    setAllLoaded(false);
    fetchData(termo, false, []);
  };

  // Handler para carregar mais
  const handleLoadMore = () => {
    if (loadingMore || allLoaded) return;
    fetchData(searchValue, true, list);
  };

  // Handler para o callback do modal de cancelamento
  const handleCancelCallback = (action: string) => {
    if (action === 'remove') {
      // Reseta e recarrega
      setAllLoaded(false);
      setSearchValue('');
      setVersaoDaBusca(versao => versao + 1);
      fetchData('', false, []);
    } else if (action === 'close') {
      setReturnRecognitionId(undefined);
    }
  };

  // Determina se deve mostrar o botão "Carregar mais"
  const showLoadMoreButton = !allLoaded && total > itemsPerPageExtrato && list.length < total;

  return (
    <div>
      <div className="mb-[22px] flex flex-wrap items-center justify-between gap-4">
        <h3 className={TITULO_DE_SECAO} tabIndex={0}>
          Reconhecimentos recebidos
          {total ? <span className={ui.CONTADOR_DA_LISTA}> ({total})</span> : null}
        </h3>
        <BuscaDePessoas
          key={versaoDaBusca}
          rotulo="Pesquise com nome, matrícula, prefixo ou grupo"
          placeholder="Pesquise com nome, matrícula, prefixo ou grupo"
          onBuscar={buscar}
          classeDoFormulario={ui.FORM_DE_BUSCA}
          classeDoCampo={ui.CAMPO_DE_BUSCA}
          antesDoCampo={<TbSearch size={18} className="shrink-0 text-brand-blue" aria-hidden="true" />}
          depoisDoCampo={
            <button type="submit" className="sr-only">
              Pesquisar
            </button>
          }
        />
      </div>

      {loadingList && <CartoesCarregando />}

      {!loadingList && (
        <>
          <div className={ui.GRADE_DE_CARTOES}>
            {!!list?.length &&
              list?.map((item: any) => (
                <article className={ui.CARTAO} key={item?._id}>
                  <div className="flex items-start gap-[18px]">
                    <div className="shrink-0">
                      <ui.AvatarDoRemetente dados={item.sender_avatar} nome={item.sender_nome_guerra || item.sender_name} matricula={item.sender} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                      <div className="flex flex-col gap-[3px]">
                        <Humanograma
                          data={{
                            ...item,
                            nome: item.sender_name,
                            funcao: item.sender_funcao,
                            _id: item.sender,
                            prefixo: item.sender_prefixo,
                            nome_dependencia: item.sender_nome_dependencia,
                          }}
                        >
                          <p className={`${ui.LINHA_DO_NOME} cursor-pointer hover:underline`}>
                            <span className={ui.NOME}>{item.sender_nome_guerra || item.sender_name.split(' ')[0]}</span>{' '}
                            <span className={ui.MATRICULA}>({item.sender})</span>
                          </p>
                        </Humanograma>
                        {!item.cancelled && <p className={`m-0 ${ui.DATA_DE_ENVIO}`}>Enviou: {ui.formatarEnvio(item.time)}</p>}
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {!!item.cancelled ? (
                          <span className={ui.CHIP_ALERTA}>
                            <TbMessageCancel size={16} aria-hidden="true" />
                            Reconhecimento cancelado pelo autor
                          </span>
                        ) : (
                          <>
                            <span className={ui.CHIP_MOEDA} tabIndex={0}>
                              {CoinLabel[item.coin]}
                            </span>
                            <span className={ui.CHIP} tabIndex={0}>
                              {BehaviorLabel[item.behavior]}
                            </span>
                            {!!item.total &&
                              (isOldVersion(item) ? (
                                <button
                                  type="button"
                                  onClick={() => handleDetail(item)}
                                  className={`${ui.CHIP_TOTAL} inline-flex items-center gap-2 border-0 hover:brightness-95`}
                                  aria-label={`Total = ${item.total} pontos. Ver detalhes`}
                                >
                                  Total = {item.total} pontos
                                  <BiInfoCircle size={18} aria-hidden="true" />
                                </button>
                              ) : (
                                <span className={ui.CHIP_TOTAL} tabIndex={0}>
                                  Total = {item.total} pontos
                                </span>
                              ))}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {!!item.message && (
                    <div className={ui.DIVISOR} tabIndex={0}>
                      <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>Reconhecimento recebido:</p>
                      <div className={ui.TEXTO_DA_MENSAGEM} dangerouslySetInnerHTML={{ __html: convertEmojis(item.message) }} />

                      {!!getComment(item) && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between">
                            <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>Comentário:</p>
                            {canEdit(getComment(item)) && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (loadingInteraction) return;

                                  setModalCommentObj({
                                    ...item,
                                    message: getComment(item).message,
                                  });
                                }}
                                className={`${ui.ACAO} text-brand-blue ${loadingInteraction ? '!cursor-wait' : ''}`}
                                disabled={loadingInteraction}
                              >
                                <FiEdit3 size={18} aria-hidden="true" /> Editar
                              </button>
                            )}
                          </div>
                          <div
                            className={ui.TEXTO_DA_MENSAGEM}
                            dangerouslySetInnerHTML={{
                              __html: convertEmojis(getComment(item).message),
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                  )}

                  {!!getReturnComment(item) && (
                    <div className={ui.DIVISOR}>
                      <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>Motivo do cancelamento:</p>
                      <div
                        className={ui.TEXTO_DA_MENSAGEM}
                        dangerouslySetInnerHTML={{
                          __html: convertEmojis(getReturnComment(item)),
                        }}
                      ></div>
                    </div>
                  )}

                  {!item.cancelled && (
                    <div className={ui.ACOES}>
                      {!!item.canReverseRecognition && (
                        <button type="button" className={ui.ACAO_ALERTA} onClick={() => setReturnRecognitionId(item.action_id)}>
                          <BsArrowReturnLeft size={20} aria-hidden="true" />
                          Devolver para remetente
                        </button>
                      )}

                      <button
                        type="button"
                        aria-pressed={hasLike(item)}
                        className={`${ui.ACAO} ${loadingInteraction ? '!cursor-wait' : 'cursor-pointer'}`}
                        onClick={() => {
                          if (loadingInteraction || user?.extra?.recognition === false) return;
                          sendInteraction(item, 'like');
                        }}
                        disabled={loadingInteraction}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={hasLike(item) ? 'fill-brand-pink-light' : 'fill-none stroke-current'}>
                          <path d="M12 21s-8-4.9-8-10.4A4.6 4.6 0 0 1 12 7a4.6 4.6 0 0 1 8 3.6C20 16.1 12 21 12 21Z" />
                        </svg>
                        {hasLike(item) ? 'Curtido!' : 'Curtir'}
                      </button>

                      {!getComment(item) && (
                        <button
                          type="button"
                          className={`${ui.ACAO} ${loadingInteraction ? '!cursor-wait' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (loadingInteraction || user?.extra?.recognition === false) return;
                            setModalCommentObj({ ...item, message: '' });
                          }}
                          disabled={loadingInteraction}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" aria-hidden="true" focusable="false">
                            <rect x="3" y="4" width="18" height="13" rx="2" />
                            <path d="m8 17-3 3.5V17" />
                          </svg>
                          Comentar
                        </button>
                      )}
                    </div>
                  )}

                  {!!item.systemMessage && (
                    <div className={`${ui.DIVISOR} text-sm italic text-content-soft`}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item.systemMessage,
                        }}
                      ></div>
                    </div>
                  )}
                </article>
              ))}

            {!list?.length && <p className={ui.VAZIO}>Nenhum reconhecimento recebido até o momento.</p>}
          </div>

          {showLoadMoreButton && (
            <div className="mt-8 flex w-full justify-center">
              <button disabled={loadingMore} className={ui.CARREGAR_MAIS} type="button" onClick={handleLoadMore}>
                {loadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </>
      )}

      {!!returnRecognitionId && (
        <ModalCancelRecognition
          callback={handleCancelCallback}
          id={returnRecognitionId}
          type="return"
        />
      )}

      {/* A moldura nova (ReactModal) já prende o foco e fecha no Esc: dispensa o ModalAcessivel. */}
      {!!modalCommentObj?._id && (
        <ModalComment
          close={() => setModalCommentObj(undefined)}
          message={modalCommentObj.message}
          sendMessage={(message, item) => {
            sendInteraction(item, 'message', message);
            setModalCommentObj(undefined);
          }}
          modalCommentObj={modalCommentObj}
        />
      )}
    </div>
  );
}
